import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Table, Form, Alert } from 'react-bootstrap';
import axios from '../utils/axiosConfig';

const BatchEditTable = ({ data, onDataChange, onModifiedCountChange }) => {
  const [editableData, setEditableData] = useState([]);
  const [modifiedRows, setModifiedRows] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [dropdownOptions, setDropdownOptions] = useState({
    stores: [],
    positions: []
  });

  // 初始化資料 - 使用ref來避免無限循環
  const dataRef = useRef(data);
  const [isInitialized, setIsInitialized] = useState(false);
  
  useEffect(() => {
    if (data && data.length > 0 && !isInitialized) {
      console.log('BatchEditTable 初始化資料:', data.length, '筆');
      setEditableData(data.map((item, index) => ({
        ...item,
        _originalIndex: index,
        _isModified: false
      })));
      setIsInitialized(true);
    }
  }, [data, isInitialized]);

  // 獲取下拉選單資料
  useEffect(() => {
    // 獲取社別列表
    axios.get('/stores')
      .then(response => {
        console.log('社別API回應:', response.data);
        setDropdownOptions(prev => ({
          ...prev,
          stores: Array.isArray(response.data) ? response.data : []
        }));
      })
      .catch(err => console.error('獲取社別列表失敗:', err));

    // 獲取職位列表
    axios.get('/positions')
      .then(response => {
        console.log('職位API回應:', response.data);
        setDropdownOptions(prev => ({
          ...prev,
          positions: Array.isArray(response.data) ? response.data : []
        }));
      })
      .catch(err => console.error('獲取職位列表失敗:', err));
  }, []);

  // 處理欄位編輯
  const handleCellEdit = useCallback((rowIndex, field, value) => {
    setEditableData(prevData => {
      const newData = [...prevData];
      const originalRow = data[newData[rowIndex]._originalIndex];
      
      // 處理外鍵中文轉換
      let processedValue = value;
      
      if (field === 'store_name' || field === 'ss_name') {
        // 社別中文轉換
        const foundStore = dropdownOptions.stores.find(store => 
          store.ss_name === value || store.ss_id.toString() === value
        );
        if (foundStore) {
          // 更新資料庫欄位 e_ssid 和顯示欄位 store_name
          newData[rowIndex].e_ssid = foundStore.ss_id;
          newData[rowIndex].store_name = foundStore.ss_name;
          console.log('社別轉換:', value, '→', foundStore);
        } else {
          // 如果找不到對應的社別，直接更新顯示欄位
          newData[rowIndex].store_name = value;
          console.log('社別未找到:', value);
        }
      } else if (field === 'position_title' || field === 'position_name' || field === 's_name') {
        // 職位中文轉換
        const foundPosition = dropdownOptions.positions.find(pos => 
          pos.s_name === value || pos.s_id.toString() === value
        );
        if (foundPosition) {
          // 更新資料庫欄位 e_position 和顯示欄位 position_title
          newData[rowIndex].e_position = foundPosition.s_id;
          newData[rowIndex].position_title = foundPosition.s_name;
          console.log('職位轉換:', value, '→', foundPosition);
        } else {
          // 如果找不到對應的職位，直接更新顯示欄位
          newData[rowIndex].position_title = value;
          console.log('職位未找到:', value);
        }
      } else {
        newData[rowIndex][field] = processedValue;
      }

      // 標記為已修改 - 只比較實際資料欄位，排除內部追蹤屬性
      const currentRowData = { ...newData[rowIndex] };
      delete currentRowData._originalIndex;
      delete currentRowData._isModified;
      
      const originalRowData = { ...originalRow };
      
      const isModified = JSON.stringify(currentRowData) !== JSON.stringify(originalRowData);
      newData[rowIndex]._isModified = isModified;
      
      console.log('修改檢測:', {
        rowIndex,
        field,
        value,
        isModified,
        currentRowData: Object.keys(currentRowData).length,
        originalRowData: Object.keys(originalRowData).length
      });
      
      // 更新修改狀態
      const newModifiedRows = new Set(modifiedRows);
      if (isModified) {
        newModifiedRows.add(rowIndex);
      } else {
        newModifiedRows.delete(rowIndex);
      }
      setModifiedRows(newModifiedRows);
      
      return newData;
    });
  }, [data, dropdownOptions, modifiedRows]);

  // 排序功能
  const handleSort = useCallback((key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  }, [sortConfig]);

  // 排序後的資料
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return editableData;
    
    return [...editableData].sort((a, b) => {
      const aVal = a[sortConfig.key] || '';
      const bVal = b[sortConfig.key] || '';
      
      if (sortConfig.direction === 'asc') {
        return aVal.toString().localeCompare(bVal.toString(), 'zh-TW');
      } else {
        return bVal.toString().localeCompare(aVal.toString(), 'zh-TW');
      }
    });
  }, [editableData, sortConfig]);

  // 通知父組件資料變化
  useEffect(() => {
    if (editableData.length > 0) {
      onDataChange(editableData);
    }
  }, [editableData]); // 移除函數依賴避免無限循環

  useEffect(() => {
    onModifiedCountChange(modifiedRows.size);
  }, [modifiedRows.size]); // 移除函數依賴避免無限循環

  // 可編輯單元格組件
  const EditableCell = ({ value, onChange, type = 'text', options = [] }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [inputValue, setInputValue] = useState(value || '');

    // console.log('EditableCell渲染:', { value, isEditing, options: options.length });

    useEffect(() => {
      setInputValue(value || '');
    }, [value]);

    const handleClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      // console.log('EditableCell點擊事件觸發，當前值:', value);
      setIsEditing(true);
    };

    const handleBlur = () => {
      // console.log('EditableCell失去焦點，保存值:', inputValue);
      setIsEditing(false);
      onChange(inputValue);
    };

    const handleKeyPress = (e) => {
      if (e.key === 'Enter') {
        handleBlur();
      }
    };

    // 編輯模式
    if (isEditing) {
      console.log('進入編輯模式，類型:', type, '選項數量:', options.length);
      if (type === 'select' && options.length > 0) {
        return (
          <Form.Select
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={handleBlur}
            size="sm"
            autoFocus
          >
            <option value="">請選擇</option>
            {options.map((option, index) => (
              <option key={index} value={option.text}>
                {option.text}
              </option>
            ))}
          </Form.Select>
        );
      } else {
        return (
          <Form.Control
            type={type}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={handleBlur}
            onKeyPress={handleKeyPress}
            size="sm"
            autoFocus
          />
        );
      }
    }

    // 顯示模式
    return (
      <div
        onClick={handleClick}
        onMouseDown={(e) => e.preventDefault()} // 防止選擇文字
        style={{
          minHeight: '30px',
          padding: '8px',
          cursor: 'pointer',
          borderRadius: '3px',
          border: '1px solid transparent',
          backgroundColor: 'transparent'
        }}
        className="hover-edit batch-edit-cell"
      >
        {value || '點擊編輯'}
      </div>
    );
  };

  // 排序圖示
  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) {
      return <span className="text-muted">⚬</span>;
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  if (!editableData.length) {
    return <Alert variant="info">沒有資料可供編輯</Alert>;
  }

  return (
    <div>
      <style>{`
        .hover-edit:hover {
          background-color: #e3f2fd !important;
          border: 1px solid #1976d2 !important;
        }
        .hover-edit {
          transition: all 0.2s ease;
        }
        .table-excel-style {
          font-size: 13px;
        }
        .table-excel-style th {
          background-color: #f8f9fa;
          border: 1px solid #dee2e6;
          padding: 8px 4px;
          user-select: none;
          cursor: pointer;
        }
        .table-excel-style td {
          border: 1px solid #dee2e6;
          padding: 2px;
          vertical-align: middle;
        }
        .modified-row {
          background-color: #fff3cd;
        }
        .batch-edit-cell {
          min-height: 30px;
          padding: 8px;
          cursor: pointer;
          border-radius: 3px;
          border: 1px solid transparent;
          background-color: transparent;
        }
        .batch-edit-cell:hover {
          background-color: #e3f2fd !important;
          border: 1px solid #1976d2 !important;
        }
      `}</style>
      
      <Table striped bordered hover className="table-excel-style">
        <thead>
          <tr>
            <th onClick={() => handleSort('e_id')}>
              ID <SortIcon column="e_id" />
            </th>
            <th onClick={() => handleSort('e_name')}>
              姓名 <SortIcon column="e_name" />
            </th>
            <th onClick={() => handleSort('store_name')}>
              社別 <SortIcon column="store_name" />
            </th>
            <th onClick={() => handleSort('position_title')}>
              職位 <SortIcon column="position_title" />
            </th>
            <th onClick={() => handleSort('e_set')}>
              組別 <SortIcon column="e_set" />
            </th>
            <th onClick={() => handleSort('e_set_position')}>
              組別職稱 <SortIcon column="e_set_position" />
            </th>
            <th onClick={() => handleSort('e_mobile')}>
              手機 <SortIcon column="e_mobile" />
            </th>
            <th onClick={() => handleSort('e_birthday')}>
              生日 <SortIcon column="e_birthday" />
            </th>
            <th onClick={() => handleSort('e_UID')}>
              身份證 <SortIcon column="e_UID" />
            </th>
            <th>狀態</th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, index) => (
            <tr key={row.e_id} className={row._isModified ? 'modified-row' : ''}>
              <td>{row.e_id}</td>
              <td>
                <EditableCell
                  value={row.e_name}
                  onChange={(value) => handleCellEdit(index, 'e_name', value)}
                />
              </td>
              <td>
                <EditableCell
                  value={row.store_name}
                  onChange={(value) => handleCellEdit(index, 'store_name', value)}
                  type="select"
                  options={dropdownOptions.stores.map(store => ({
                    value: store.ss_id,
                    text: store.ss_name
                  }))}
                />
              </td>
              <td>
                <EditableCell
                  value={row.position_title}
                  onChange={(value) => handleCellEdit(index, 'position_title', value)}
                  type="select"
                  options={dropdownOptions.positions.map(pos => ({
                    value: pos.s_id,
                    text: pos.s_name
                  }))}
                />
              </td>
              <td>
                <EditableCell
                  value={row.e_set || ''}
                  onChange={(value) => handleCellEdit(index, 'e_set', value)}
                />
              </td>
              <td>
                <EditableCell
                  value={row.e_set_position || ''}
                  onChange={(value) => handleCellEdit(index, 'e_set_position', value)}
                />
              </td>
              <td>
                <EditableCell
                  value={row.e_mobile}
                  onChange={(value) => handleCellEdit(index, 'e_mobile', value)}
                />
              </td>
              <td>
                <EditableCell
                  value={row.e_birthday}
                  onChange={(value) => handleCellEdit(index, 'e_birthday', value)}
                  type="date"
                />
              </td>
              <td>
                <EditableCell
                  value={row.e_UID || ''}
                  onChange={(value) => handleCellEdit(index, 'e_UID', value)}
                />
              </td>
              <td>
                <span className={`badge ${row.e_status === 0 ? 'bg-success' : 'bg-danger'}`}>
                  {row.e_status === 0 ? '啟用' : '停用'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default BatchEditTable;